import { Button, Col, Form, Input, message, Modal } from "antd";
import DragUpload from "common/components/uploadComponent/dragger";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Client from "redux/models/client";
import { selectUser, selectUserId } from "redux/slices/userSlice";
import K from "utilities/constants";
import { noTrailingSpaceAllowedRule } from "utilities/generalUtility";
import { useParams } from "react-router-dom";
import { DataTableLayout } from "../../../../common/components/dataTableLayout/dataTableLayout";
import { usePageFilter } from "../../../../hooks/usePageFilters";
import { ClientSideDataGrid } from "../../../../common/components/clientSideDataGrid/clientSideDataGrid";
import { getColumns } from "./columns";

export default function Attachments() {
  const { id } = useParams();

  const userId = useSelector(selectUserId);
  const userSlice = useSelector(selectUser);

  const [form] = Form.useForm();
  const uploadRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState({});

  const [attachments, setAttachments] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [file, setFile] = useState(null);

  const [search, setSearch] = usePageFilter(`client_${id}_attachment_search`);

  const downloadFile = async (url) => {
    const downloadUrl = await Client.getPreSignedUrl(url);
    window.open(downloadUrl);
  };

  const deleteAttachment = async (record) => {
    try {
      await Client.deleteAttachement(record.id);
      await Client.deleteFileFromS3(record.attachment);
      getAllAttachments();
      message.success("Attachment Deleted Successfully");
    } catch (error) {
      console.log(error);
      message.error("Something went wrong");
    }
  };

  const onFileChange = (selectedFile, error) => {
    setFile(selectedFile);
    form.setFields([{ name: "document", errors: error ? [error] : [] }]);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const afterModalClose = () => {
    setEditData({});
    setFile(null);
    form.resetFields();
    uploadRef.current?.reset();
  };

  const onFinish = async (values) => {
    setIsLoading(true);

    try {
      const res = await Client.uploadAttachement(
        file,
        K.S3Folders.ClientAttachments + id,
      );

      const payload = {
        entityId: id, // Client Id
        type: K.AttachmentType.Client,
        title: values.title,
        attachment: res.filepath,
      };

      await Client.createAttachment(payload);
      getAllAttachments();
      setIsLoading(false);
      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      setIsLoading(false);
      console.error(err);
    }
  };

  const getAllAttachments = async () => {
    try {
      const body = { entityId: id, type: K.AttachmentType.Client };
      const res = await Client.getAttachment(body);
      setAttachments(res);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [editData]);

  useEffect(() => {
    getAllAttachments();
  }, []);

  return (
    <>
      <DataTableLayout
        searchDefaultValue={search}
        onSearchChange={setSearch}
        actions={[
          {
            permissions: [K.Permissions.AddAttchment],
            renderer: () => (
              <Col>
                <Button type="primary" onClick={showModal}>
                  Create
                </Button>
              </Col>
            ),
          },
        ]}
      >
        <ClientSideDataGrid
          userId={userId}
          configKey={K.AgGridTable.Keys.AttachmentVisibleColumn}
          defaultColumnConfig={getColumns(
            userSlice.roles,
            downloadFile,
            deleteAttachment,
          )}
          rowData={attachments}
          quickFilterText={search}
        />
      </DataTableLayout>

      <Modal
        forceRender
        className="s2-theme-style"
        title="Add Documents"
        open={isModalVisible}
        centered
        closeIcon={<i className="icon-closeable" />}
        okText="Add"
        wrapClassName="vertical-center-modal"
        onOk={handleOk}
        okButtonProps={{ loading: isLoading }}
        onCancel={handleCancel}
        afterClose={afterModalClose}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={editData}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: "Title is required." },
              noTrailingSpaceAllowedRule(),
            ]}
          >
            <Input placeholder="Enter Title" />
          </Form.Item>
          <Form.Item
            label="Document"
            name="document"
            rules={[
              {
                required: true,
                validator: (_, obj) => {
                  if (file) {
                    return Promise.resolve();
                  }
                  return Promise.reject("Please upload documents!");
                },
              },
            ]}
          >
            <DragUpload
              ref={uploadRef}
              onFileChange={onFileChange}
              allowedTypes={K.FileUpload.Groups.DOCUMENTS_AND_IMAGES}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
